/**
 * Typed surface over the SloboZip WASM module.
 *
 * The Emscripten-generated `slobozip.js` exposes a global `Module` and a
 * set of `_wasm_*` exports. We dynamically load that script from the
 * public folder, then wrap the raw exports in a typed API.
 *
 * This file mirrors the legacy `web/wasm_api.js` exactly in behavior,
 * just typed and module-scoped instead of a global IIFE.
 */

export type ArchiveFile = {
  name: string;
  data: Uint8Array;
};

export type ArchiveListEntry = {
  name: string;
  original: number;
  compressed: number;
  timestamp: number;
};

export type ArchiveExtractEntry = {
  name: string;
  size: number;
  data: Uint8Array;
};

export type SlobozipApi = {
  version: () => string;
  compress: (data: Uint8Array) => Uint8Array;
  decompress: (data: Uint8Array, originalSize: number) => Uint8Array;
  createArchive: (files: ArchiveFile[]) => Uint8Array;
  extractArchive: (data: Uint8Array) => ArchiveExtractEntry[];
  listArchive: (data: Uint8Array) => ArchiveListEntry[];
};

/* ── Internal: Emscripten module shape ─────────────────────────────── */

type EmscriptenFS = {
  mkdir: (p: string) => void;
  rmdir: (p: string) => void;
  unlink: (p: string) => void;
  writeFile: (p: string, data: Uint8Array | string) => void;
  readFile: (p: string) => Uint8Array;
  readdir: (p: string) => string[];
};

type EmscriptenModule = {
  HEAPU8: Uint8Array;
  FS: EmscriptenFS;
  calledRun?: boolean;
  onRuntimeInitialized?: () => void;
  ccall: (
    name: string,
    returnType: 'string' | 'number' | null,
    argTypes: Array<'string' | 'number'>,
    args: Array<string | number>,
  ) => string | number;
  setValue: (ptr: number, value: number, type: string) => void;
  getValue: (ptr: number, type: string) => number;
  writeArrayToMemory: (data: Uint8Array, ptr: number) => void;
  stringToUTF8: (str: string, ptr: number, max: number) => void;
  UTF8ToString: (ptr: number) => string;
  _wasm_alloc: (n: number) => number;
  _wasm_free: (ptr: number) => void;
};

declare global {
  // eslint-disable-next-line no-var
  var Module: EmscriptenModule | undefined;
}

/* ── Loader ────────────────────────────────────────────────────────── */

let _modulePromise: Promise<EmscriptenModule> | null = null;

/**
 * Load the WASM module. Idempotent — first call kicks off the script
 * fetch, subsequent calls share the same promise.
 *
 * Resolves with the Emscripten module ready to use.
 */
export function loadSlobozip(): Promise<SlobozipApi> {
  if (_modulePromise) return _modulePromise.then(buildApi);

  _modulePromise = new Promise<EmscriptenModule>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-slobozip]');
    if (existing) {
      // Script already present (e.g. HMR re-render). Wait for runtime init.
      waitForRuntime(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.src = import.meta.env.BASE_URL + 'slobozip.js';
    script.async = true;
    script.dataset.slobozip = 'true';
    script.onload = () => waitForRuntime(resolve, reject);
    script.onerror = () =>
      reject(
        new Error(
          'Failed to load slobozip.js. Build the WASM module first: ' +
            'emcmake cmake -B build-wasm web && emmake cmake --build build-wasm',
        ),
      );
    document.head.appendChild(script);
  });

  return _modulePromise.then(buildApi);
}

function waitForRuntime(
  resolve: (m: EmscriptenModule) => void,
  reject: (e: Error) => void,
) {
  if (typeof globalThis.Module === 'undefined') {
    reject(new Error('slobozip.js loaded but Module global is missing'));
    return;
  }
  const M = globalThis.Module;

  if (M.calledRun) {
    resolve(M);
    return;
  }

  const prev = M.onRuntimeInitialized;
  M.onRuntimeInitialized = () => {
    if (prev) prev();
    resolve(M);
  };
}

/* ── API construction (mirrors legacy wasm_api.js) ─────────────────── */

function buildApi(M: EmscriptenModule): SlobozipApi {
  const allocBytes = (data: Uint8Array): number => {
    const ptr = M._wasm_alloc(data.length);
    M.writeArrayToMemory(data, ptr);
    return ptr;
  };

  const readBytes = (ptr: number, len: number): Uint8Array =>
    new Uint8Array(M.HEAPU8.buffer, ptr, len).slice();

  return {
    version() {
      return M.ccall('wasm_version', 'string', [], []) as string;
    },

    compress(data) {
      const bound = M.ccall(
        'wasm_compress_bound',
        'number',
        ['number'],
        [data.length],
      ) as number;

      const inPtr = allocBytes(data);
      const outPtr = M._wasm_alloc(bound);
      const lenPtr = M._wasm_alloc(4);
      M.setValue(lenPtr, bound, 'i32');

      const status = M.ccall(
        'wasm_compress',
        'number',
        ['number', 'number', 'number', 'number'],
        [inPtr, data.length, outPtr, lenPtr],
      ) as number;

      const outLen = M.getValue(lenPtr, 'i32');
      const result = status === 0 ? readBytes(outPtr, outLen) : null;

      M._wasm_free(inPtr);
      M._wasm_free(outPtr);
      M._wasm_free(lenPtr);

      if (!result) throw new Error(`Compression failed (status ${status})`);
      return result;
    },

    decompress(data, originalSize) {
      const inPtr = allocBytes(data);
      const outPtr = M._wasm_alloc(originalSize);
      const lenPtr = M._wasm_alloc(4);
      M.setValue(lenPtr, originalSize, 'i32');

      const status = M.ccall(
        'wasm_decompress',
        'number',
        ['number', 'number', 'number', 'number', 'number'],
        [inPtr, data.length, outPtr, lenPtr, originalSize],
      ) as number;

      const outLen = M.getValue(lenPtr, 'i32');
      const result = status === 0 ? readBytes(outPtr, outLen) : null;

      M._wasm_free(inPtr);
      M._wasm_free(outPtr);
      M._wasm_free(lenPtr);

      if (!result) throw new Error(`Decompression failed (status ${status})`);
      return result;
    },

    createArchive(files) {
      const FS = M.FS;
      const tmpDir = '/tmp/slz_input';
      const outPath = '/tmp/output.slbz';

      try { FS.rmdir(tmpDir); } catch {}
      try { FS.unlink(outPath); } catch {}
      try { FS.mkdir(tmpDir); } catch {}

      const paths: string[] = [];
      for (const file of files) {
        const safeName = file.name.replace(/[/\\]/g, '_');
        const path = tmpDir + '/' + safeName;
        FS.writeFile(path, file.data);
        paths.push(path);
      }

      const ptrArray = M._wasm_alloc(paths.length * 4);
      for (let i = 0; i < paths.length; i++) {
        const strPtr = M._wasm_alloc(paths[i].length + 1);
        M.stringToUTF8(paths[i], strPtr, paths[i].length + 1);
        M.setValue(ptrArray + i * 4, strPtr, 'i32');
      }

      const outPathPtr = M._wasm_alloc(outPath.length + 1);
      M.stringToUTF8(outPath, outPathPtr, outPath.length + 1);

      const status = M.ccall(
        'wasm_archive_create',
        'number',
        ['number', 'number', 'number'],
        [outPathPtr, ptrArray, paths.length],
      ) as number;

      let result: Uint8Array | null = null;
      if (status === 0) result = new Uint8Array(FS.readFile(outPath));

      for (let i = 0; i < paths.length; i++) {
        M._wasm_free(M.getValue(ptrArray + i * 4, 'i32'));
        try { FS.unlink(paths[i]); } catch {}
      }
      M._wasm_free(ptrArray);
      M._wasm_free(outPathPtr);
      try { FS.unlink(outPath); } catch {}
      try { FS.rmdir(tmpDir); } catch {}

      if (!result) throw new Error(`Archive creation failed (status ${status})`);
      return result;
    },

    extractArchive(archiveData) {
      const FS = M.FS;
      const arcPath = '/tmp/input.slbz';
      const destDir = '/tmp/slz_output';

      try { FS.unlink(arcPath); } catch {}
      try { FS.rmdir(destDir); } catch {}
      try { FS.mkdir(destDir); } catch {}
      FS.writeFile(arcPath, archiveData);

      const arcPtr = M._wasm_alloc(arcPath.length + 1);
      M.stringToUTF8(arcPath, arcPtr, arcPath.length + 1);
      const destPtr = M._wasm_alloc(destDir.length + 1);
      M.stringToUTF8(destDir, destPtr, destDir.length + 1);

      const status = M.ccall(
        'wasm_archive_extract',
        'number',
        ['number', 'number'],
        [arcPtr, destPtr],
      ) as number;

      M._wasm_free(arcPtr);
      M._wasm_free(destPtr);

      if (status !== 0) {
        try { FS.unlink(arcPath); } catch {}
        throw new Error(`Extraction failed (status ${status})`);
      }

      const results: ArchiveExtractEntry[] = [];
      try {
        const entries = FS.readdir(destDir).filter((n) => n !== '.' && n !== '..');
        for (const name of entries) {
          const data = FS.readFile(destDir + '/' + name);
          results.push({ name, size: data.length, data: new Uint8Array(data) });
          FS.unlink(destDir + '/' + name);
        }
      } catch {}

      try { FS.unlink(arcPath); } catch {}
      try { FS.rmdir(destDir); } catch {}
      return results;
    },

    listArchive(archiveData) {
      const FS = M.FS;
      const arcPath = '/tmp/list_input.slbz';

      try { FS.unlink(arcPath); } catch {}
      FS.writeFile(arcPath, archiveData);

      const arcPtr = M._wasm_alloc(arcPath.length + 1);
      M.stringToUTF8(arcPath, arcPtr, arcPath.length + 1);

      const jsonPtr = M.ccall(
        'wasm_archive_list_json',
        'number',
        ['number'],
        [arcPtr],
      ) as number;

      const jsonStr = M.UTF8ToString(jsonPtr);
      M._wasm_free(jsonPtr);
      M._wasm_free(arcPtr);
      try { FS.unlink(arcPath); } catch {}

      const parsed = JSON.parse(jsonStr);
      if (parsed.error) throw new Error('Failed to read archive');
      return parsed as ArchiveListEntry[];
    },
  };
}
